import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui';
import { ConfirmModal } from '../../Modal/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../hooks/useToast';
import {
  useCancelBatch,
  useClusteringBatch,
  useDeleteBatch,
  useReclusterBatch,
  useRerunBatch,
} from '../../../hooks/useClustering';
import type { ReclusterRequest } from '../../../types/clustering';
import { canMutate } from '../shared';
import { LiveProgress } from '../Progress/LiveProgress';
import { ClustersTable } from '../Results/ClustersTable';
import { KeywordsTable } from '../Results/KeywordsTable';
import { BatchOverview } from './BatchOverview';
import { ExportMenu } from './ExportMenu';
import { ReclusterModal } from './ReclusterModal';
import { userMessage } from '../../../utils/userMessage';
import styles from '../Clustering.module.css';

interface BatchDetailViewProps {
  projectId: string;
  batchId: string;
}

export function BatchDetailView({ projectId, batchId }: BatchDetailViewProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const mutate = canMutate(user);

  const { batch, isLoading, isTerminal, error } = useClusteringBatch(batchId);
  const reclusterMut = useReclusterBatch();
  const rerunMut = useRerunBatch();
  const cancelMut = useCancelBatch();
  const deleteMut = useDeleteBatch();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reclusterOpen, setReclusterOpen] = useState(false);
  const [tab, setTab] = useState<'overview' | 'progress' | 'clusters' | 'keywords'>(
    isTerminal ? 'overview' : 'progress',
  );

  const handleRerun = async () => {
    try {
      const next = await rerunMut.mutateAsync(batchId);
      toast.success('Batch cloned', 'Starting a new run with the same inputs.');
      navigate(`/clustering/${projectId}/batches/${next.id}`);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409) {
        toast.warning(
          'Cannot rerun',
          'The original keyword list is no longer stored. Submit a new batch.',
        );
      } else {
        toast.error('Rerun failed', (err as Error).message);
      }
    }
  };

  const handleRecluster = async (settings: ReclusterRequest) => {
    if (reclusterMut.isPending) return;
    try {
      const next = await reclusterMut.mutateAsync({ id: batchId, body: settings });
      toast.success('Re-cluster started', 'Using the same SERP data for 0 credits.');
      setReclusterOpen(false);
      navigate(`/clustering/${projectId}/batches/${next.id}`);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409) {
        toast.warning(
          'Cannot re-cluster',
          'Stored SERP data is unavailable. Run a fresh rerun instead.',
        );
      } else {
        toast.error('Re-cluster failed', (err as Error).message);
      }
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMut.mutateAsync(batchId);
      toast.success('Cancel requested');
      setCancelOpen(false);
    } catch (err) {
      toast.error('Cancel failed', (err as Error).message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMut.mutateAsync(batchId);
      toast.success('Batch deleted');
      navigate(`/clustering/${projectId}`);
    } catch (err) {
      toast.error('Delete failed', (err as Error).message);
    }
  };

  if (isLoading && !batch) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading batch…</div>
      </div>
    );
  }
  if (error && !batch) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{userMessage(error, 'Could not load this batch. Please try again.')}</div>
      </div>
    );
  }
  if (!batch) return null;

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate(`/clustering/${projectId}`)}
      >
        ← Back to batches
      </button>

      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>
            {batch.name ?? `Batch ${batch.id.slice(0, 8)}`}
          </h1>
          <p className={styles.subtitle}>
            {batch.total_keywords.toLocaleString()} keywords · status {batch.status}
            {batch.parent_batch_id && (
              <>
                {' · '}
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() =>
                    navigate(
                      `/clustering/${projectId}/batches/${batch.parent_batch_id}`,
                    )
                  }
                  title="Open the original batch this rerun was cloned from"
                >
                  rerun of {batch.parent_batch_id.slice(0, 8)}
                </button>
              </>
            )}
          </p>
        </div>
        <div className={styles.headerActions}>
          <ExportMenu
            batchId={batch.id}
            batchName={batch.name}
            disabled={!isTerminal || batch.status !== 'done'}
          />
          {mutate && (
            <>
              {!isTerminal && (
                <Button
                  variant="secondary"
                  onClick={() => setCancelOpen(true)}
                  disabled={cancelMut.isPending}
                >
                  Cancel
                </Button>
              )}
              {isTerminal && batch.status === 'done' && (
                <Button
                  variant="secondary"
                  onClick={() => setReclusterOpen(true)}
                  loading={reclusterMut.isPending}
                >
                  Re-cluster (0 credits)
                </Button>
              )}
              {isTerminal && (
                <Button variant="primary" onClick={handleRerun} loading={rerunMut.isPending}>
                  Rerun fresh SERPs
                </Button>
              )}
              <Button
                variant="danger"
                onClick={() => setDeleteOpen(true)}
                loading={deleteMut.isPending}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="clusters">
            Clusters ({batch.clusters_count.toLocaleString()})
          </TabsTrigger>
          <TabsTrigger value="keywords">Keywords</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <BatchOverview batch={batch} canEdit={mutate} />
        </TabsContent>
        <TabsContent value="progress">
          <LiveProgress batch={batch} />
        </TabsContent>
        <TabsContent value="clusters">
          <ClustersTable batchId={batch.id} canEdit={mutate} />
        </TabsContent>
        <TabsContent value="keywords">
          <KeywordsTable batchId={batch.id} totalKeywords={batch.total_keywords} />
        </TabsContent>
      </Tabs>

      <ConfirmModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancel}
        title="Cancel batch?"
        message="Cooperative cancel stops the pipeline at the next safe checkpoint. Credits already consumed are not refunded."
        confirmText="Cancel batch"
        variant="danger"
      />
      <ReclusterModal
        isOpen={reclusterOpen}
        batch={batch}
        isSubmitting={reclusterMut.isPending}
        onClose={() => setReclusterOpen(false)}
        onConfirm={handleRecluster}
      />
      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete batch?"
        message={`"${batch.name ?? `Batch ${batch.id.slice(0, 8)}`}" and all its clusters, keywords, and results will be permanently removed. This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}
